import { grey } from '@mui/material/colors'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { Photograph } from '@styled-icons/heroicons-outline/Photograph'
import { ShieldExclamation } from '@styled-icons/heroicons-outline/ShieldExclamation'
import React, { Children, useCallback, useEffect, useRef, useState } from 'react'

import WorkOrdersPhotos from 'components/drawer/work-orders-photos/WorkOrdersPhotos'
import ExceptionIcon from 'components/exceptions/ExceptionIcon'
import StyledBadge from 'components/notes/common/StyledBadge'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'

import { getExceptionDate, getExceptionDelayTimeFormatted } from './utils'

// work-orders-photos is still untyped JS, so its props infer `never[]` from their defaults.

// Leaving the badge schedules the close instead of firing it, so the pointer can travel
// into the popover paper (which cancels it) — that's what makes the list scrollable.
const CLOSE_DELAY_MS = 150

const toDisplayScalar = (value: unknown) => (value == null || typeof value === 'object' ? null : String(value))

export type ExceptionBadgeException = {
  exceptionType?: string | null
  fileLocation?: string | null
  metadata?: { extra?: unknown } | null
  noteText?: string | null
}

type ExceptionBadgeProps = {
  exceptions?: ExceptionBadgeException[]
  onClick?: ((event: React.MouseEvent<HTMLElement>) => void) | null
  onExceptionClick?: ((exception: ExceptionBadgeException) => void) | null
}

const ExceptionBadge = ({ exceptions = [], onClick = null, onExceptionClick = null }: ExceptionBadgeProps) => {
  const [viewPopoverAnchorEl, setViewPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [isOpenPhotosDialog, setIsOpenPhotosDialog] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const exceptionsCount = exceptions.length
  const hasExceptions = exceptionsCount > 0
  const exceptionsWithAttachment = exceptions.filter(exception => exception.fileLocation)
  const hasExceptionsWithAttachment = exceptionsWithAttachment.length > 0

  const cancelScheduledClose = useCallback(() => clearTimeout(closeTimerRef.current), [])

  // Index off the row so the map doesn't rebuild a closure per item on every render.
  const handleExceptionClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => onExceptionClick?.(exceptions[Number(event.currentTarget.dataset.index)]),
    [exceptions, onExceptionClick]
  )

  const handleViewPopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation()
      cancelScheduledClose()
      setViewPopoverAnchorEl(event.currentTarget)
    },
    [cancelScheduledClose]
  )

  const handleScheduledClose = useCallback(() => {
    cancelScheduledClose()
    closeTimerRef.current = setTimeout(() => setViewPopoverAnchorEl(null), CLOSE_DELAY_MS)
  }, [cancelScheduledClose])

  const handleViewPopoverClose = useCallback(() => {
    cancelScheduledClose()
    setViewPopoverAnchorEl(null)
  }, [cancelScheduledClose])

  const handleOpenPhotosDialog = useCallback(() => setIsOpenPhotosDialog(true), [])
  const handleClosePhotosDialog = useCallback(() => setIsOpenPhotosDialog(false), [])

  useEffect(() => () => clearTimeout(closeTimerRef.current), [])

  return (
    <>
      <IconButton
        disabled={!onClick && !hasExceptions}
        size="small"
        {...(hasExceptions && { onMouseEnter: handleViewPopoverOpen, onMouseLeave: handleScheduledClose })}
        {...(onClick ? { onClick } : hasExceptionsWithAttachment && { onClick: handleOpenPhotosDialog })}
      >
        <StyledBadge badgeContent={exceptionsCount}>
          <SvgIcon>
            <ShieldExclamation />
          </SvgIcon>
        </StyledBadge>
      </IconButton>
      <Popover
        disableRestoreFocus
        anchorEl={viewPopoverAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={Boolean(viewPopoverAnchorEl)}
        // The root stays click-through so the hover popover never blocks the page; the paper
        // itself is interactive (scrollable) and keeps the popover alive while hovered.
        slotProps={{
          paper: {
            sx: { maxHeight: 320, overflowY: 'auto', pointerEvents: 'auto' },
            onMouseEnter: cancelScheduledClose,
            onMouseLeave: handleScheduledClose,
          },
        }}
        sx={{ pointerEvents: 'none', zIndex: Z_INDEX.BADGE_POPOVER }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={handleViewPopoverClose}
      >
        <List>
          {Children.toArray(
            exceptions.map((exception, index) => {
              const { exceptionType = '', noteText = '', fileLocation } = exception
              const exceptionDate = getExceptionDate(exception)
              const { onlyDate, onlyTime } = convertDateTimeToPreferedTimezone({ dateTime: exceptionDate })
              const delayTimeFormatted = getExceptionDelayTimeFormatted(exception)
              const extra = toDisplayScalar(exception.metadata?.extra)

              return (
                <ListItem
                  data-index={index}
                  divider={index !== exceptionsCount - 1}
                  {...(onExceptionClick && { sx: { cursor: 'pointer' }, onClick: handleExceptionClick })}
                  secondaryAction={
                    fileLocation && (
                      <SvgIcon color="action" fontSize="small">
                        <Photograph />
                      </SvgIcon>
                    )
                  }
                >
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
                    <ExceptionIcon color="action" exception={exceptionType} fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <Typography fontWeight={400} variant="h6">
                          {exceptionType}
                        </Typography>
                        {exceptionDate && <Typography variant="caption">{`${onlyDate} | ${onlyTime}`}</Typography>}
                        {delayTimeFormatted && (
                          <Typography bgcolor={grey[100]} borderRadius={1} fontWeight={400} p={0.75} variant="h6" width="fit-content">
                            {delayTimeFormatted}
                          </Typography>
                        )}
                        {extra != null && (
                          <Typography fontWeight={400} variant="h6">
                            {`${T.NUMBER_OF_EXTRAS}: ${extra}`}
                          </Typography>
                        )}
                        <Typography fontWeight={400} variant="h6">
                          {noteText}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              )
            })
          )}
        </List>
      </Popover>
      <WorkOrdersPhotos exceptions={exceptionsWithAttachment} isOpenDrawer={isOpenPhotosDialog} onClose={handleClosePhotosDialog} />
    </>
  )
}

export default ExceptionBadge
